import React, { useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { Button, SubmitButton } from "../shared/Button"
import { useMutation } from "react-query"
import { mutationRequest } from "../../util/request"
import { LegalLanguage } from "./LegalLanguage"
import type { AuthFlowData } from "./AuthenticationDialog"
import { checkEmailForIssues, EmailIssue } from "../../util/emailSanityCheck"

interface RegistrationFormProps {
  setModalState: (state: "auth" | "register" | "reset_password") => void
  validationError: (data: AuthFlowData) => string | null
  closeDialog: () => void
  defaultBandType?: string | null
  followBandId?: string | number | null
}

interface RegistrationFormValues {
  email: string
  password: string
  first_name: string
  last_name: string
  band_name: string
  band_type: string
  time_zone: string
}

const bandTypes = [
  { value: "fan", label: "Fan" },
  { value: "band", label: "Band" },
  { value: "individual", label: "Solo Performer" },
  { value: "venue", label: "Venue" },
  { value: "blog", label: "Blog" },
  { value: "promoter", label: "Promoter" },
] as const

const getBandTypeRef = (bandType: string) => {
  return bandTypes.find((bt) => bt.value === bandType)
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  setModalState,
  validationError,
  closeDialog,
  defaultBandType = null,
  followBandId = null,
}) => {
  const urlParams = new URLSearchParams(window.location.search)
  const initBandType = urlParams.get("band-type")
  const initEmailAddress = urlParams.get("email")
  const initBandName = urlParams.get("band-name")

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegistrationFormValues>({
    defaultValues: {
      email: initEmailAddress || "",
      password: "",
      first_name: "",
      last_name: "",
      band_name: initBandName || "",
      band_type: defaultBandType || initBandType || "fan",
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  })

  const [registerErrorMessage, setRegisterErrorMessage] = useState("")
  const mutation = useMutation(mutationRequest)
  const isSubmitting = mutation.isLoading

  const bandType = watch("band_type")

  const [emailIssue, setEmailIssue] = useState<EmailIssue | null>(null)
  const [emailConfirmed, setEmailConfirmed] = useState(false)
  const pendingDataRef = useRef<RegistrationFormValues | null>(null)

  const submitRegistration = (data: RegistrationFormValues) => {
    const registerData: AuthFlowData = {
      flow: "register",
      current_location: window.location.href,
      referral_token: localStorage.getItem("referral_token") ?? undefined,
      follow_band_id: followBandId ?? undefined,
      user: {
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        band_name: data.band_name,
        band_type: data.band_type,
        time_zone: data.time_zone,
      },
    }

    setRegisterErrorMessage("")

    const errorMessage = validationError(registerData)
    if (errorMessage) {
      setRegisterErrorMessage(errorMessage)
      return
    }

    try {
      mutation.mutate({
        options: { method: "POST" },
        path: `/api/v1/users`,
        data: registerData,
      })
    } catch {
      setRegisterErrorMessage("Sorry, there was an unknown error")
    }
  }

  const onSubmit = (data: RegistrationFormValues) => {
    if (!emailConfirmed) {
      const issue = checkEmailForIssues(data.email)
      if (issue) {
        pendingDataRef.current = data
        setEmailIssue(issue)
        return
      }
    }
    submitRegistration(data)
  }

  useEffect(() => {
    if (mutation.status === "success") {
      const data = mutation.data as {
        success?: boolean
        token?: string
        redirect_location?: string
        errors?: { error: string }
      }
      if (data.success) {
        localStorage.setItem("jwt_token", data.token ?? "")
        closeDialog()
        window.location.href = data.redirect_location ?? "/"
      } else {
        setRegisterErrorMessage(data.errors?.error ?? "Unknown error")
      }
    } else if (mutation.status === "error") {
      setRegisterErrorMessage("Sorry, there was an unknown error")
    }
  }, [mutation.status, mutation.data, closeDialog])

  const bandTypeRef = getBandTypeRef(bandType)

  return (
    <>
      <div className="lg:col-span-9 col-span-12 lg:pr-6">
        <h1 className="border-b-4 border-black font-bold mb-4 text-xl uppercase">
          Sign Up
        </h1>
        {registerErrorMessage ? (
          <div className="text-red-700 text-sm mb-2">
            {registerErrorMessage}
          </div>
        ) : null}
        <div className="mb-2">
          <p className="italic text-sm">
            We recommend setting up a band account with a band email, and
            separate fan accounts for each person in a band
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            {bandTypes.map((bandTypeOption) => (
              <label
                key={bandTypeOption.value}
                className="flex lg:inline-flex items-center lg:mr-6 mb-1 lg:mb-0"
              >
                <input
                  type="radio"
                  className="form-radio"
                  value={bandTypeOption.value}
                  {...register("band_type")}
                />
                <span className="ml-2 text-sm text-gray-700">
                  {bandTypeOption.label}
                </span>
              </label>
            ))}
            <div className="text-xs text-gray-600">
              don't worry, you can always change your mind about who you are ;)
            </div>

            <label
              className="mt-4 block text-gray-700 text-xs uppercase font-bold"
              htmlFor="email"
            >
              Email Address
            </label>
            <input
              className="shadow appearance-none border rounded-sm w-full text-base py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="email"
              placeholder="Email"
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
                onChange: () => {
                  setEmailConfirmed(false)
                  setEmailIssue(null)
                },
              })}
            />
            {errors.email && (
              <p className="text-red-700 text-xs mt-1">
                {errors.email.message}
              </p>
            )}
            {emailIssue && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-300 rounded text-sm">
                <div className="text-gray-800 mb-2">{emailIssue.message}</div>
                <div className="flex gap-2">
                  {emailIssue.suggestion ? (
                    <>
                      <button
                        type="button"
                        className="px-3 py-1.5 bg-green-700 text-white rounded text-xs font-semibold focus:outline-none"
                        onClick={() => {
                          const data = pendingDataRef.current
                          if (!data) return
                          setValue("email", emailIssue.suggestion!)
                          setEmailConfirmed(true)
                          setEmailIssue(null)
                          submitRegistration({...data, email: emailIssue.suggestion!})
                        }}
                      >
                        Use {emailIssue.suggestion}
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1.5 border border-gray-300 rounded text-xs font-semibold focus:outline-none"
                        onClick={() => {
                          const data = pendingDataRef.current
                          if (!data) return
                          setEmailConfirmed(true)
                          setEmailIssue(null)
                          submitRegistration(data)
                        }}
                      >
                        No, use {pendingDataRef.current?.email}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="px-3 py-1.5 bg-green-700 text-white rounded text-xs font-semibold focus:outline-none"
                        onClick={() => {
                          const data = pendingDataRef.current
                          if (!data) return
                          setEmailConfirmed(true)
                          setEmailIssue(null)
                          submitRegistration(data)
                        }}
                      >
                        Continue anyway
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1.5 border border-gray-300 rounded text-xs font-semibold focus:outline-none"
                        onClick={() => setEmailIssue(null)}
                      >
                        Let me fix it
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="mb-4">
            <label
              className="block text-gray-700 text-xs uppercase font-bold"
              htmlFor="password"
            >
              Password
            </label>
            <input
              className="shadow appearance-none border rounded-sm w-full text-base py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="password"
              placeholder="******************"
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
                maxLength: {
                  value: 24,
                  message: "Password must be no more than 24 characters",
                },
              })}
            />
            {errors.password && (
              <p className="text-red-700 text-xs mt-1">
                {errors.password.message}
              </p>
            )}
          </div>
          {bandType === "fan" ? (
            <>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-xs uppercase font-bold"
                  htmlFor="first_name"
                >
                  First Name
                </label>
                <input
                  className="shadow appearance-none border rounded-sm w-full text-base py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  type="text"
                  placeholder="First Name"
                  {...register("first_name", {
                    required:
                      bandType === "fan" ? "First name is required" : false,
                  })}
                />
                {errors.first_name && (
                  <p className="text-red-700 text-xs mt-1">
                    {errors.first_name.message}
                  </p>
                )}
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-xs uppercase font-bold"
                  htmlFor="last_name"
                >
                  Last Name
                </label>
                <input
                  className="shadow appearance-none border rounded-sm w-full text-base py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  type="text"
                  placeholder="Last Name"
                  {...register("last_name", {
                    required:
                      bandType === "fan" ? "Last name is required" : false,
                  })}
                />
                {errors.last_name && (
                  <p className="text-red-700 text-xs mt-1">
                    {errors.last_name.message}
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="mb-4">
                <label
                  className="mt-2 block text-gray-700 text-xs uppercase font-bold"
                  htmlFor="band_name"
                >
                  {bandTypeRef?.label ?? bandType} Name
                </label>
                <input
                  className="shadow appearance-none border rounded-sm w-full text-base py-2 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  type="text"
                  placeholder={`${bandType} Name`}
                  {...register("band_name", {
                    required: bandType !== "fan" ? "Name is required" : false,
                  })}
                />
                {errors.band_name && (
                  <p className="text-red-700 text-xs mt-1">
                    {errors.band_name.message}
                  </p>
                )}
              </div>
            </>
          )}

          <div className="flex items-center justify-between lg:mt-4 mt-8">
            <SubmitButton
              className="disabled:cursor-not-allowed"
              active={!isSubmitting}
            >
              {isSubmitting ? "Signing Up..." : "Sign Up"}
            </SubmitButton>
          </div>
          <div className="mt-6">
            <LegalLanguage />
          </div>
        </form>
      </div>
      <div className="lg:col-span-3 col-span-12 lg:mt-0 mt-16">
        <h1 className="border-b-4 border-black font-bold mb-4 text-xl text-right">
          Hip to bandNada?
        </h1>
        <div className="float-right mt-4">
          <Button
            className="disabled:cursor-not-allowed"
            disabled={isSubmitting}
            onClick={() => setModalState("auth")}
          >
            Sign In
          </Button>
        </div>
      </div>
    </>
  )
}
